// src/ui/Sidebar.jsx — chat list, search, presence
import React, { useEffect, useRef, useState } from 'react';
import SupporterBadge from './SupporterBadge';   // 💛 the Supporter badge, next to the name
import { useChatLock, chatNeedsUnlock, subscribeChatLock } from '../services/chatLock';
import BrandLottie from './BrandLottie';
import { Search, Logout, Play, Verified, MapPin, Mic, Poll, Doc, Phone, Plane, Reply, Pin, Link, More, Trash, ChevronRight, Group as GroupIcon, CheckCheck, Slash, Check, Gear, Timer, Info } from './icons';
import {
  togglePin, setArchived, deleteChatForMe, deleteAndExitGroup, isArchived, hasExitedGroup,
} from '../services/chatActions';
import { docStyle } from '../services/documents';
import { getUserProfile, watchPresence, searchUsers, watchUserProfile } from '../services/users';
import { derivePresenceVisibility, isViewerInTargetContacts } from '../services/presenceVisibility';
import { markAllRead } from '../services/markAllRead';
import NewGroupModal from './NewGroupModal';
import BlockedUsersModal from './BlockedUsersModal';
import GuestLinkModal from './GuestLinkModal';
import { isGuestChat, guestChatExpired } from '../services/guestLinks';
import StartChatModal from './StartChatModal';
import MyQRModal from './MyQRModal';
import SearchResults from './SearchResults';
import ListEditorModal from './ListEditorModal';
import { watchChatLists, applyFilter } from '../services/chatLists';
import { searchAll } from '../services/searchAll';
import { otherParticipant, isNoteToSelf, clearedAtMs, tsMs, unreadFor, newerPreview, isPinned, lastActivityMs, liveSystemMarker, messageMissing, eventMs, reactionLine } from '../services/chats';
import { renderCallPreviewText, callInputs } from '../services/callRender';
import { watchTyping, activityVerb } from '../services/typing';
import { Mic as MicIcon, Image as ImageIcon, Video as VideoIcon, Doc as DocIcon } from './icons';
import { timeLabel, chatPreviewFrom } from './format';
import { hiddenIdsFor } from '../services/hiddenMessages';
import { openSupport, isOfficialChat } from '../services/support';
import Avatar, { resolveName, groupTitle } from './Avatar';
import MessageTicks, { deriveReceiptStatus } from './MessageTicks';
import { watchChatPreviews } from '../services/previews';
import { fetchFloorMs, joinedMsOf } from '../services/joinFloor';   // 👥 a member added later starts at their join
import { useDraft, bindDrafts, draftPreviewLine } from '../services/drafts';   // 📝 "Draft: …" on a row
import VaultButton from './VaultButton';

const ROW_ACT_ICON = { recording: MicIcon, photo: ImageIcon, video: VideoIcon, album: ImageIcon, document: DocIcon };

// a small inbox-tray glyph — the icon set has no archive mark
const ArchiveIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 16} height={p.height || 16} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={p.style}>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <path d="M10 12h4" />
  </svg>
);

// a QR mark — the icon set has none
const QrIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 18} height={p.height || 18} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={p.style}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
  </svg>
);

// a funnel — the icon set has no filter mark
const Filter = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 16} height={p.height || 16} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={p.style}>
    <path d="M4 5h16l-6.4 7.6V19l-3.2-2v-4.4L4 5Z" />
  </svg>
);

/* 🟢 A CALL IS AN EVENT, NOT A MESSAGE SOMEBODY SENT YOU. Both shapes it can
   arrive in are named here rather than in the JSX, so the chat list and anything
   else that needs the distinction ask the same question. */
function isCallRow(m) {
  if (!m) return false;
  return m.systemType === 'call_history' || m.type === 'call' || !!m.callData;
}

function ChatRow({ chat, myUid, myName, myPrivacy, active, onClick, onClosed, syncedForChat, preview, typers, draggable, blocking, myReadAt, syncing }) {
  bindDrafts(myUid);
  // 📝 typed here and not sent — the row says "Draft: …" (not while the chat is the open one)
  const _draft = useDraft(chat.id);
  const lockHidden = useChatLock(chat.id).needs;   // 🔒
  const draft = active ? '' : _draft;
  const [profile, setProfile] = useState(null);
  const [online, setOnline] = useState(false);
  // 🟢 the green dot is a PRIVACY DECISION, not a presence reading: their global
  //    Last Seen / Online settings, their per-chat switch toward me, and my own
  //    reciprocity all have a say. Same gate the header uses — see
  //    services/presenceVisibility.js.
  const [isContact, setIsContact] = useState(false);
  // ── hover actions: pin / archive / delete, synced to the app's fields ──
  const [menu, setMenu] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) { setMenu(false); setConfirming(false); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);
  const otherUid = otherParticipant(chat, myUid);
  // a blocked pair shows no presence and no typing — same as the open chat's header
  const rowBlockedByMe = !!(blocking && otherUid && blocking.byMe.has(otherUid)) && !chat.isGroup;
  const rowBlockedMe = !!(blocking && otherUid && blocking.me.has(otherUid)) && !chat.isGroup;
  const rowBlocked = rowBlockedByMe || rowBlockedMe;

  useEffect(() => {
    let live = true;
    if (chat.isGroup) return; // group renders from chat fields directly
    // watch, don't fetch-once: a privacy change on their side must reach the dot
    const unsubP = otherUid ? watchUserProfile(otherUid, (p) => live && setProfile(p)) : null;
    if (otherUid) getUserProfile(otherUid).then((p) => live && setProfile((cur) => cur || p));
    const unsub = (otherUid && !rowBlocked) ? watchPresence(otherUid, (s) => live && setOnline(!!s?.online)) : null;
    return () => { live = false; unsub && unsub(); unsubP && unsubP(); };
  }, [otherUid, chat.isGroup, rowBlocked]);

  // only consulted when they chose 'my_contacts' — the common path costs nothing
  useEffect(() => {
    let live = true;
    if (!otherUid || profile?.privacySettings?.lastSeen !== 'my_contacts') { setIsContact(false); return undefined; }
    isViewerInTargetContacts(otherUid, myUid).then((v) => live && setIsContact(v));
    return () => { live = false; };
  }, [otherUid, myUid, profile?.privacySettings?.lastSeen]);

  const { canSeeOnline } = derivePresenceVisibility({
    viewerPrivacy: myPrivacy,
    targetPrivacy: profile?.privacySettings,
    viewerUid: myUid,
    isContact,
    perChatLastSeen: otherUid ? chat?.lastSeenBy?.[otherUid] : undefined,
    perChatOnline: otherUid ? chat?.onlineBy?.[otherUid] : undefined,
  });
  const showDot = online && canSeeOnline && !chat.isGroup;

  // group title lives in chat.name on mobile; else the peer's real name (participantNames as fallback)
  /* 🟥 THE MARKER ONLY COUNTS WHILE THE CHAT DOCUMENT STILL DESCRIBES IT.
     These two used to be read straight off `chat.lastMessageSystem`, and they
     outrank the real message preview in the row below — so a marker that a
     later writer forgot to retire pinned the row to "Abdullah voted: A" for
     good, with the newer text message rendered nowhere. See liveSystemMarker
     in services/chats.js for why a timestamp comparison cannot decide this. */
  const sys = liveSystemMarker(chat);
  const sysVote = sys?.type === 'vote' ? sys : null;
  const sysCall = sys?.type === 'call_history' ? sys : null;
  const callText = sysCall ? renderCallPreviewText(callInputs(sysCall, myUid)) : null;

  /* ═════════════════════════════════════════════════════════════════════════
     🟥 THE TIME MUST DESCRIBE WHATEVER THE ROW IS DESCRIBING.

     `preview` is the newest MESSAGE — and a live marker deliberately outranks
     it, so the row's sentence and the row's clock were coming from two
     different events. A group call is the case where that comes apart
     completely: when it ends, the server stamps the chat document and writes
     no message at all, because the phones keep their call rows in SQLite and
     this browser keeps its own in localCallRows. So `preview` stayed on
     yesterday's chatter while the marker correctly announced a call that ended
     a minute ago — the row jumped to the top of the list (the sort reads
     `lastActivityMs`, which was right) and was stamped "yesterday" beside it.

     🟢 THE MARKER'S TIME IS THE CHAT DOCUMENT'S. That is the write that
        created it. Falling back to `preview.ts` only if the document carries no
        usable stamp at all, so a marker on an old document degrades to the old
        behaviour instead of to blank.
     ═════════════════════════════════════════════════════════════════════════ */
  const hidNewest = preview?.hidNewest === true;
  const hiddenHere = hidNewest ? hiddenIdsFor(chat.id) : null;
  const exported = chatPreviewFrom(chat, syncedForChat, {
    hidden: hiddenHere, myUid, ignoreChatDoc: hidNewest,
  });
  /* 🟥 THE ROW USED TO PREFER THE DOCUMENT AND NEVER LOOK AT THE CLOCK.
     `preview?.text || fallbackText` — and because store-and-forward deletes
     documents unevenly (the receiving phone, on chat open, never the reader's
     own messages), the surviving document is routinely OLDER than the newest
     message the export knows about. A day of texting from the phone landed in
     the export and was rendered nowhere, while the list went on quoting the
     last thing this browser had sent. See newerPreview in services/chats.js. */
  const shown = newerPreview(
    { id: preview?.id || null, text: preview?.text || '', ts: preview?.ts ? tsMs(preview.ts) : 0, senderId: preview?.senderId || null, source: 'live' },
    exported,
  ) || {};
  /* 🟢 THE RICH PARTS BELONG TO THE DOCUMENT, so they are drawn only when the
     document is what the row is describing. A thumbnail, a reply quote or a
     tick from a message the row is no longer quoting is worse than none: it
     describes something the reader cannot see. */
  const fromLive = shown.source === 'live';
  /* 👍 A REACTION NEWER THAN THE MESSAGE THE ROW WOULD QUOTE OWNS THE ROW — its
     line ("You reacted ❤️ to …") and its time. A message sent after it takes the
     row back, being the newer of the two. See eventMs in services/chats.js. */
  const sysReact = sys?.type === 'reaction' ? sys : null;
  const reactMs = sysReact ? eventMs(chat) : 0;
  const reactOwns = !!sysReact && reactMs > 0 && reactMs > (Number(shown.ts) || 0);
  const reactText = reactOwns ? reactionLine(sysReact, {
    isMe: chat.lastMessageSenderId === myUid,
    lookupText: (id) => {
      if (preview?.id && String(preview.id) === id && preview.text) return preview.text;
      const hit = Array.isArray(syncedForChat) ? syncedForChat.find((x) => String(x.id || x.clientTempId) === id) : null;
      return hit ? (hit._plain || hit.text || '') : '';
    },
  }) : '';
  /* 🟥 THE ROW HELD THE PHOTO AND DREW A GLYPH. The rich parts — thumbnails, the
     "+N", a reply quote — were built only for the LIVE document, so a row quoting
     the phone's exported history fell back to "📷 Photo". That is every message
     store-and-forward has swept, and ALL of the official account's, which is
     exactly where it was noticed.

     🟢 The rule the comment below states was always right; it was only ever
     applied to one of the two witnesses. Whichever one the row is quoting now
     supplies its own parts, so the two look identical. */
  const rich = reactOwns ? null : (fromLive ? preview : (exported?.thumbs?.length ? exported : null));
  const activityMs = lastActivityMs(chat);
  /* 🟢 THE TIME BELONGS TO WHATEVER THE ROW IS QUOTING — `shown`, not the
     document. The row was already capable of standing in the right position
     (the sort reads the chat doc, which is fresh) with the wrong text AND the
     wrong clock beside it. */
  const rowTimeMs = reactOwns ? reactMs : (sys ? (activityMs || shown.ts) : (shown.ts || activityMs));
  const callMissed = sysCall ? /^Missed/.test(callText || '') : false;

  // 🟢 "No messages yet" — the app says this whenever a chat has nothing left to show.
  //    Three ways a chat gets there and the web missed all of them: cleared from
  //    ChatInfoScreen (the Firestore docs SURVIVE, so the old preview kept showing),
  //    every message deleted for me, or simply brand new. clearedAt is the per-user
  //    watermark the phone writes for the first case.
  const clearedMs = clearedAtMs(chat, myUid);
  /* 🟢 THE WATERMARK IS ASKED ABOUT WHATEVER THE ROW IS SHOWING. Reading the
     document's stamp while the row quotes the export would clear a row whose
     visible message is newer than the clear, and keep one whose isn't. */
  const previewMs = Number(shown.ts) || 0;
  const clearedOut = !!clearedMs && !!previewMs && previewMs <= clearedMs;
  // 🟥 RECENCY IS THE FIX, AND ORDER WAS THE BUG. There is no "live first, export
  //    second" any more — `shown` has already answered which of the two describes
  //    the newest message (newerPreview, services/chats.js). What is left below is
  //    only the last resort: the padlock, from a FLAG rather than from a truthy
  //    "🔒 Message" string sitting in preview.text, which would win outright and
  //    hide a perfectly readable export behind it.
  //
  //    🟥 THAT FLAG DID NOT EXIST UNTIL NOW. This comment described an intention;
  //    previews.js was still writing the literal padlock into `text`, so the two
  //    fallbacks below were unreachable for every chat whose last message could
  //    not be decrypted — which, after the server's sweep, is most of them on a
  //    fresh login. The visible symptom was "Fetching from your phone…" appearing
  //    on Note to Self alone: the one chat with no message to fail on.
  /* 🟥 "🔒 Message" IS NOT THE ANSWER WHILE THE HISTORY IS STILL ARRIVING. A 1:1
     message document is deleted by the server's store-and-forward sweep once it
     has been delivered, so on a fresh login the row genuinely has nothing to
     quote — the real text is in the export the phone is still building. The
     padlock says "this cannot be read", which is a statement about the message;
     the truth is "this has not arrived yet", which is a statement about the
     sync. The proof that they are different: open the chat, come back, and the
     row is fine — nothing was ever unreadable.

     So while the sync is running the row says what is actually happening, and
     the padlock is kept for the case it was written for: a message that HAS
     arrived and could not be decrypted. */
  /* 🟥 A DELETED MESSAGE MUST NOT COME BACK THROUGH A FALLBACK. previews.js used
     to report a message I had deleted as `text: ''`, and '' is falsy — so this
     chain read it as "no preview computed yet" and went looking elsewhere. Both
     of the elsewheres are ignorant of the deletion: the phone's exported history
     is a snapshot taken before it, and `chats/{id}.lastMessage` is a SHARED field
     that a delete-for-me is not allowed to touch. The result was that deleting
     the last message in a chat put its text, or "📷 Photo", straight back into
     the row — and never showed "No messages yet".

     `hidNewest` is the missing distinction. It says the newest document in
     Firestore is one I removed, so the two fallbacks are describing it and must
     be filtered (the export) or skipped (the chat doc). It is deliberately NOT
     `empty`: a chat with no documents is the ordinary state after the server's
     sweep, and there those same fallbacks are the only truth the row has. */
  const bodyText = rowBlockedByMe ? 'You blocked this user'
    : rowBlockedMe ? 'This user blocked you'
    : reactOwns ? reactText
    : shown.text
    || (syncing ? '' : (preview?.unreadable ? '🔒 Message' : ''));
  // 🟥 THE BADGE READS unreadCounts, VETOED BY readUpTo. The count itself has to come
  //    from the chat doc rather than from counting messages, because the server's
  //    store-and-forward sweep deletes 1:1 message docs once they're delivered — there
  //    is often nothing left here to count. readUpTo is the correction: if this account
  //    has read past the newest message (on the phone, or in another tab), the stored
  //    count is stale and the row shows nothing. That's what makes "read it on my
  //    phone, watch it clear on the web" work without any extra plumbing.
  /* 🟥 AND THE VETO WAS ANSWERED ABOUT THE WRONG MESSAGE. unreadFor() returns 0
     the moment the newest sender is me — so a row pinned to my own surviving
     document showed NO BADGE at all, however much had arrived since. The sender
     has to be the sender of the message the row is actually describing. */
  const unread = clearedOut ? 0 : unreadFor(chat, myUid, previewMs, shown.senderId, myReadAt);
  const pinned = isPinned(chat, myUid);
  /* 🟥 "FETCHING FROM YOUR PHONE…" WAS A GLOBAL BROADCAST. `syncing` is one flag
     for the whole sync, handed to every row, so while any export was in flight
     EVERY row without text announced that something was coming for it —
     including chats that have never had a message and never will have one. That
     is the "it shows on chats that really have nothing new" report, and it is
     not a timing problem: the row was answering a question about the account
     with a sentence about itself.

     🟢 A ROW EXPECTS SOMETHING ONLY IF THE CHAT SAYS SO. The chat document's
     activity stamp is written by every sender and is never swept, so a chat
     with a stamp and no text we can read is genuinely missing something. A chat
     with no stamp is empty, and "No messages yet" is the truthful line for it. */
  /* 🟥 …AND "ACTIVITY" WAS NOT ENOUGH EITHER. The stamp is bumped by a reaction,
     a vote, a screenshot marker, a call ending — none of which an export can
     bring. A row expects a MESSAGE only when the document names one the row is
     not quoting; see messageMissing in services/chats.js. */
  const expectsMore = !clearedOut && messageMissing(chat, shown);
  /* 🟥 …AND "No messages yet" MUST STILL BE REACHABLE DURING A SYNC. `!syncing`
     alone hid it for every row while any export ran, which is what left a row
     with nothing to say rendering nothing at all. */
  const isEmpty = !sysVote && !sysCall && (clearedOut || !bodyText) && !(syncing && expectsMore);
  /* 🟢 THE ROW CAN PROVE IT IS BEHIND, so it says so without throwing away what
     it has. The chat document's activity stamp is written by every sender and
     is never swept; when it is later than the newest message the row can quote,
     something exists here that this browser has not been given. A dot beside the
     old text is the honest rendering of that — replacing the text with
     "Fetching…" would hide the last thing we do know in order to announce that
     there is something we don't. */
  /* 🟥 THE CLOCK ALONE SAID "BEHIND" FOR A CHAT WHOSE LAST EVENT WAS A REACTION,
     every time the sync ran — a dot on one chat and not another, with nothing
     to fetch. The same rule as expectsMore now: a real message, named by the
     document, that this row is not quoting. */
  const behind = !!bodyText && expectsMore;

  const name = chat.isGroup
    ? groupTitle(chat)
    : isNoteToSelf(chat, myUid)
      ? 'Note to Self'
      : (resolveName(profile) !== 'Allo user' ? resolveName(profile) : (chat.participantNames?.[otherUid] || 'Allo user'));
  return (
    <div
      className={`chat-row ${active ? 'active' : ''}`}
      onClick={onClick}
      draggable={!!draggable}
      onDragStart={(e) => {
        // a custom MIME type, so the conversation pane can tell a chat apart from a file
        // while it's still only allowed to see `types` — and text/plain alongside it so
        // dropping into a text field somewhere does something sane instead of nothing
        e.dataTransfer.setData('text/allo-chat', chat.id);
        e.dataTransfer.setData('text/plain', name || 'Chat');
        e.dataTransfer.effectAllowed = 'copy';
      }}
    >
      <Avatar profile={profile} chat={chat} peerUid={otherUid} online={showDot} myUid={myUid}
        /* 🧑‍🎨 the same two facts the dot is drawn from */
        live={{ online: !!online, canSeeOnline: !!canSeeOnline, viewKey: `row:${otherUid || chat.id}` }} />
      <div className="chat-meta">
        <div className="chat-name-row">
          {/* name + badge share one flex box so the timestamp stays hard right —
              three loose children under space-between would strand the badge mid-row */}
          <span className="chat-name-wrap">
            <span className="chat-name">{name}</span>
            {isNoteToSelf(chat, myUid) && <Verified />}
            {!chat.isGroup && otherUid ? <SupporterBadge uid={otherUid} size={14} /> : null}
            {/* 💬 THE OFFICIAL ACCOUNT IS MARKED WHEREVER ITS NAME APPEARS.
                The badge was only on the chat header, which is the one surface
                you reach AFTER deciding the row is genuine — exactly backwards.
                A row claiming to be Allo is the thing worth verifying, because
                an account calling itself "Allo" is trivial to create. */}
            {isOfficialChat(chat) && (
              <span className="official-badge" title="Official Allo account">
                <Verified width={14} height={14} />
              </span>
            )}
            {/* 🔗 a guest chat is temporary, and the row says so — otherwise it
                looks like every other conversation right up until the day it
                disappears */}
            {isGuestChat(chat) && (
              <span className={`temp-chip ${guestChatExpired(chat) ? 'dead' : ''}`}
                title={guestChatExpired(chat) ? 'This temporary chat has ended' : 'Temporary chat'}>
                <Timer width={11} height={11} />
              </span>
            )}
          </span>
          {/* 🟥 THE SAME DEAD FIELD THE SORT WAS USING. The fallback here read
              `chat.lastMessageTime`, which almost no document carries any more —
              and timeLabel() answers a missing value with Date.now(), so a row
              whose preview had not resolved yet displayed the CURRENT time and
              silently claimed the conversation had just happened. Falling back to
              the same activity stamp the list is ordered by keeps the time beside
              a row honest, and consistent with the row's position. */}
          <span className="chat-time">{timeLabel(rowTimeMs)}</span>
        </div>
        <div className="chat-preview-row">
          <span className="chat-preview" style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            {/* 🔒 a chat locked on the phone says nothing here — not its last line, not a photo, not who is typing */}
            {lockHidden ? <span className="row-locked" style={{ color: 'var(--text-2)' }}>🔒 Locked chat</span> : (<>
            {/* 🟢 my own last message shows its ticks in the list, like the app */}
            {/* 🟥 EXCEPT A CALL, WHICH NOBODY DELIVERED OR READ. A call row carries
                a senderId — whoever started it — so it satisfied "my own last
                message" and drew delivery ticks beside "Video call 0:55". There is
                no such thing as a call being delivered or seen: the ticks were
                answering a question the row never asked, and on a missed call they
                actively contradicted it. */}
            {!draft && rich && rich.senderId === myUid && !isCallRow(rich) && (
              <MessageTicks status={deriveReceiptStatus(rich, chat, myUid)} size={14} color="var(--tick-grey)" readColor="var(--tick-read)" />
            )}
            {/* 🟢 the actual picture, not a 📷 glyph — up to 4 tiles then "+N" */}
            {/* 🟢 "Abdullah voted: Yes" — like the app, this is driven by the CHAT
                doc's lastMessageSystem, not by any message, and it outranks the
                message preview while it stands (a new message deleteField()s it). */}
            {/* 🟢 typing outranks everything — it is the only live thing in the row.
                1-on-1 says just "typing"; a group names who, because "typing" is
                useless when eight people share the thread. Same split the app makes. */}
            {typers?.length ? (() => {
              const t = typers[0];
              const kind = t.activity && t.activity !== 'typing' ? t.activity : null;
              const ActIcon = kind ? (ROW_ACT_ICON[kind] || null) : null;
              const label = kind
                ? (chat.isGroup ? `${t.name} is ${activityVerb(kind)}` : activityVerb(kind))
                : (chat.isGroup ? `${t.name} is typing` : 'typing');
              return (
                <>
                  {ActIcon && <ActIcon width={13} height={13} style={{ color: kind === 'recording' ? 'var(--danger)' : 'var(--allo-blue)', flexShrink: 0 }} />}
                  <span className="row-typing">{label}</span>
                  {!ActIcon && <span className="ty-dots sm"><i /><i /><i /></span>}
                </>
              );
            })() : draft ? (
              /* 📝 a draft outranks the last message (typing still outranks it) */
              <>
                <span className="row-draft-label">Draft:</span>
                <span className="row-draft">{draftPreviewLine(draft)}</span>
              </>
            ) : (syncing && !bodyText && expectsMore) ? (
              /* 🟢 THE DOT, AND ONLY THE DOT. The words used to be here too —
                 "Fetching from your phone…" — and read as a per-chat status
                 that came and went for reasons the list never explained. The
                 sync has ONE status and it is the pill above the list; a row
                 only marks that it, specifically, is still owed a message. It
                 replaces itself the moment the export lands — `shown` and
                 `syncedForChat` already drive this expression. */
              <span className="row-fetching" title="Fetching from your phone…">
                <span className="row-fetching-dot" />
              </span>
            ) : isEmpty ? (
              <span className="chat-empty">No messages yet</span>
            ) : sysCall ? (
              <>
                <Phone width={13} height={13} style={{ color: callMissed ? 'var(--danger)' : 'var(--text-3)', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: callMissed ? 'var(--danger)' : undefined }}>
                  {callText}
                </span>
              </>
            ) : sysVote ? (
              <>
                <Poll width={13} height={13} style={{ color: 'var(--allo-blue)', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <b style={{ fontWeight: 600 }}>{sysVote.name}</b>{' voted: '}{sysVote.targetText}
                </span>
              </>
            ) : rich?.reply ? (
              <span className="row-reply">
                <Reply width={13} height={13} style={{ flexShrink: 0, color: 'var(--text-3)' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {rich.reply.mine
                    ? 'You replied to: '
                    : `${(chat.isGroup ? (chat.participantNames?.[rich.senderId] || 'User') : name)} replied to: `}
                  <q>{rich.reply.snippet}</q>
                </span>
              </span>
            ) : rich?.route ? (
              /* 🟢 DTW ───✈─── LAX — the same graphical route the app's
                 ChatsTab draws, rather than a "\u2708\ufe0f Flight Info: AA123" string. The
                 hairlines flex, so the codes stay pinned to the edges of the row. */
              <span className="row-flight">
                <b>{rich.route.from}</b>
                <span className="rf-line" />
                <Plane width={13} height={13} className="rf-plane" />
                <span className="rf-line" />
                <b>{rich.route.to}</b>
              </span>
            ) : (<>
            {behind && syncing && <span className="row-fetching-dot" title="Updating from your phone…" />}
            {rich?.icon === 'flight' && <Plane width={13} height={13} style={{ color: 'var(--allo-blue)', flexShrink: 0, transform: 'rotate(90deg)' }} />}
            {rich?.icon === 'doc' && <Doc width={13} height={13} style={{ color: docStyle(rich.text).color, flexShrink: 0 }} />}
            {rich?.icon === 'poll' && <Poll width={13} height={13} style={{ color: 'var(--allo-blue)', flexShrink: 0 }} />}
            {rich?.icon === 'pin' && <MapPin width={13} height={13} style={{ color: 'var(--danger)', flexShrink: 0 }} />}
            {rich?.icon === 'mic' && <Mic width={13} height={13} style={{ color: 'var(--text-3)', flexShrink: 0 }} />}
            {rich?.icon === 'link' && <Link width={13} height={13} style={{ color: 'var(--text-2)', flexShrink: 0 }} />}
            {rich?.thumbs?.length ? (
              <span className="preview-thumbs">
                {rich.thumbs.map((t, i) => (
                  <span className="pt" key={i}>
                    {/* phones ship a blurred base64 thumb on media messages; a
                        shared post card carries a plain URL instead — both end
                        up as the same little tile */}
                    {t.b64
                      ? <img src={`data:image/jpeg;base64,${t.b64}`} alt="" />
                      : t.url
                        ? <img src={t.url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                        : <span className="pt-blank" />}
                    {t.video && <span className="pt-play"><Play width={7} height={7} /></span>}
                  </span>
                ))}
                {rich.extra > 0 && <span className="pt pt-more">+{rich.extra}</span>}
              </span>
            ) : null}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {bodyText}
            </span>
            </>)}
            </>)}
          </span>
          {pinned && <Pin width={14} height={14} className="row-pin" />}
          {unread > 0 && <span className="unread-badge">{unread > 99 ? '99+' : unread}</span>}
        </div>
      </div>

      {/* ── the hover actions: ⋯ opens pin / archive / delete ── */}
      <div className="row-actions" ref={menuRef} onClick={(e) => e.stopPropagation()}>
        <button
          className={`row-menu-btn ${menu ? 'open' : ''}`} title="Chat options"
          onClick={() => { setMenu(!menu); setConfirming(false); }}
        >
          <More width={16} height={16} />
        </button>
        {menu && (
          <div className="row-menu">
            {confirming ? (
              <>
                <div className="row-menu-warn">
                  {chat.isGroup && !hasExitedGroup(chat, myUid)
                    ? 'Leave this group and delete the chat?'
                    : 'Delete this chat?'}
                </div>
                <button
                  className="row-menu-item danger"
                  onClick={() => {
                    setMenu(false); setConfirming(false);
                    const run = chat.isGroup
                      ? deleteAndExitGroup(chat, myUid, myName)
                      : deleteChatForMe(chat, myUid);
                    run.catch(() => {});
                    if (active) onClosed && onClosed();
                  }}
                >
                  <Trash width={15} height={15} />
                  {chat.isGroup && !hasExitedGroup(chat, myUid) ? 'Delete & exit' : 'Delete'}
                </button>
                <button className="row-menu-item" onClick={() => setConfirming(false)}>Cancel</button>
              </>
            ) : (
              <>
                <button
                  className="row-menu-item"
                  onClick={() => { setMenu(false); togglePin(chat, myUid).catch(() => {}); }}
                >
                  <Pin width={15} height={15} /> {pinned ? 'Unpin chat' : 'Pin chat'}
                </button>
                <button
                  className="row-menu-item"
                  onClick={() => {
                    setMenu(false);
                    setArchived(chat, myUid, !isArchived(chat, myUid)).catch(() => {});
                    if (active && !isArchived(chat, myUid)) onClosed && onClosed();
                  }}
                >
                  <ArchiveIcon width={15} height={15} /> {isArchived(chat, myUid) ? 'Unarchive chat' : 'Archive chat'}
                </button>
                <button className="row-menu-item danger" onClick={() => setConfirming(true)}>
                  <Trash width={15} height={15} />
                  {chat.isGroup && !hasExitedGroup(chat, myUid) ? 'Delete & exit' : 'Delete chat'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟢 SHORT, AND ONLY WHEN IT IS TRUE.

   The old copy was a paragraph — "Your phone hasn't answered — open Allo on it
   (same account) and keep it on screen" — and it was wrong most of the times it
   appeared, because the browser could not tell a throttled phone, a phone
   halfway through a large export, or a phone whose reply was still in flight
   from a phone that was switched off. It accused all of them.

   With the acknowledgement in the protocol these states are now distinct, so
   each line can be short enough to read at a glance and still be accurate:

     · 'requested' — nothing has come back yet. This is the ONLY state where
       "waiting for your phone" is a true sentence.
     · 'acked'     — a phone answered. Whatever happens next, it is not absent.
     · 'building'  — the phone's own export counter, with a real percentage.
     · 'stalled'   — nothing answered. Four words and a button, not a paragraph:
       there is nothing to explain that a retry doesn't say better.

   And the whole pill only ever appears on a FIRST fill or an explicit Retry.
   A background refresh of history already on screen says nothing at all. */
const SYNC_LABEL = {
  requested: 'Waiting for your phone…',
  acked: 'Your phone is answering…',
  building: 'Preparing…',
  downloading: 'Downloading…',
  opening: 'Opening chats…',
  synced: 'History updated',
  cached: '',
  stalled: 'Open Allo on your phone',
  error: 'Couldn’t sync history',
  declined: 'Your phone didn’t share your chats',
};

/** The two states a person can actually do something about — they get the button. */
const SYNC_FAILED = (s) => s === 'stalled' || s === 'error' || s === 'declined';

export default function Sidebar({ myUid, myProfile, chats, selectedChatId, onSelectChat, onStartChat, onOpenChatId, onHome, onLogout, syncStatus, syncProgress, syncedHistory, onRetrySync, draggableRows, blocking, onListScroll, myReads = {} }) {
  const [term, setTerm] = useState('');
  const [people, setPeople] = useState([]);
  // 🟢 archived chats fold into their own section, exactly like the app's list
  const [showArchived, setShowArchived] = useState(false);
  const archivedChats = chats.filter((c) => isArchived(c, myUid));
  const myName = myProfile?.fullName || myProfile?.username || 'A member';
  const myPrivacy = myProfile?.privacySettings;
  // ── the ⋯ menu: create group · mark all read · blocked users ──
  const [menu, setMenu] = useState(false);
  const [makeGroup, setMakeGroup] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [showGuest, setShowGuest] = useState(false);   // 🔗 the guest-link manager
  const [marking, setMarking] = useState('');
  /* 🟢 One toast, two writers. `marking` is a progress string the mark-all-read
     flow owns; `notice` is a one-off message anything else can post. They share
     the `.sb-toast` element rather than stacking two boxes on top of each other. */
  const [notice, setNotice] = useState('');
  const [helpBusy, setHelpBusy] = useState(false);
  /* A toast that never leaves stops being a toast and becomes furniture — the
     eye edits it out, and the next one says nothing. */
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 4500);
    return () => clearTimeout(t);
  }, [notice]);
  const [startChat, setStartChat] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // ── the filter bar: All / Unread / Groups + my own lists ──
  const [lists, setLists] = useState([]);
  const [filter, setFilter] = useState('all');
  const [editList, setEditList] = useState(null);   // {} = new, {…} = manage
  useEffect(() => watchChatLists(myUid, setLists), [myUid]);
  // a list deleted (here or on the phone) must not stay selected
  useEffect(() => {
    if (filter === 'all' || filter === 'unread' || filter === 'groups') return;
    if (!lists.some((l) => l.id === filter)) setFilter('all');
  }, [lists, filter]);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const away = (e) => { if (!menuRef.current?.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [menu]);

  const doMarkAll = async () => {
    setMenu(false);
    setMarking('working');
    const { cleared, suppressed } = await markAllRead(chats, myUid);
    setMarking(cleared
      ? `Marked ${cleared} chat${cleared === 1 ? '' : 's'} read${suppressed ? ` · ${suppressed} without a receipt` : ''}`
      : 'Nothing unread');
    setTimeout(() => setMarking(''), 3200);
  };
  // 🟢 live decrypted previews (chat.lastMessage in Firestore is only a lock placeholder)
  const [previews, setPreviews] = useState({});
  const [typing, setTyping] = useState({});
  useEffect(() => watchTyping(setTyping), []);
  const chatIdsKey = chats.map((c) => c.id).join(',');
  /* 👥 each group's join floor, read by the preview listeners (services/joinFloor) */
  const joinFloorsRef = useRef({});
  joinFloorsRef.current = Object.fromEntries(chats.map((c) => [c.id, fetchFloorMs(joinedMsOf(c, myUid), 0)]));
  const joinFloorsKey = chats.map((c) => `${c.id}:${joinFloorsRef.current[c.id] || 0}`).join(',');
  // 🟢 A REF, NOT A DEPENDENCY. The preview listeners need the phone's exported history
  //    to read a message this browser has no key for — but history lands minutes after
  //    the list does, and putting it in the dep array would tear down and rebuild one
  //    Firestore listener per chat every time it refreshed.
  /* 🟢 "STILL COMING" IS A PROPERTY OF THE SYNC, NOT OF A ROW. These four states
     are the ones where the phone has been asked and has not finished answering;
     `synced` and `cached` mean the export is here, and the two failure states
     mean it is never coming — in both cases a row showing "fetching…" for ever
     would be a lie. See SYNC_LABEL for the same list from the pill's side. */
  /* 🟢 'acked' JOINS THE LIST. It is a new rung between "asked" and "building" —
     the phone has confirmed it heard us but has not started publishing yet — and
     leaving it out would make every row stop saying "fetching…" for the seconds
     between the ack and the first progress write, then start again. */
  const historyPending = ['requested', 'acked', 'building', 'downloading', 'opening'].includes(syncStatus);

  const syncedRef = useRef(syncedHistory);
  useEffect(() => { syncedRef.current = syncedHistory; }, [syncedHistory]);
  useEffect(() => {
    const ids = chatIdsKey ? chatIdsKey.split(',') : [];
    if (!ids.length) return;
    const unsub = watchChatPreviews(
      ids,
      myUid,
      (chatId, p) => setPreviews((prev) => ({ ...prev, [chatId]: p })),
      (chatId) => syncedRef.current?.[chatId],
      (chatId) => joinFloorsRef.current?.[chatId] || 0,
    );
    return () => unsub && unsub();
  }, [chatIdsKey, myUid, joinFloorsKey]);

  // 🟥 DECLARED HERE, NOT AT THE TOP. This reads `previews`, `lists` and
  //    `filter`, all declared below the menu block — evaluating it earlier is a
  //    TDZ ReferenceError ("cannot access before initialization") on first render.
  /* 🟥 THE FILTER HAS TO ASK THE SAME QUESTION THE ROW DOES. This read the
     surviving document alone, so a chat pinned to my own old document answered
     "the newest sender is me" and dropped out of Unread — while its row, now
     ranking by time, was showing somebody else's message with a badge on it.
     Two answers to one question is worse than either answer. */
  const hasUnread = (c) => {
    const shown = newerPreview(
      { text: previews[c.id]?.text || '', ts: previews[c.id]?.ts ? tsMs(previews[c.id].ts) : 0, senderId: previews[c.id]?.senderId || null, source: 'live' },
      chatPreviewFrom(c, syncedHistory?.[c.id], { myUid }),
    ) || {};
    return unreadFor(c, myUid, Number(shown.ts) || 0, shown.senderId, myReads[c.id]) > 0
      || c.manuallyUnread?.[myUid] === true;
  };
  const visibleChats = applyFilter(
    chats.filter((c) => !isArchived(c, myUid)),
    filter, lists, hasUnread,
  );

  // 🔎 EVERYTHING SEARCH. Archived chats are searched too — hiding a chat from
  //    the list is not the same as saying "never find this again". Deleted and
  //    cleared content is excluded inside searchAll(), where the four deletion
  //    rules live.
  const [lockVer, setLockVer] = useState(0);
  useEffect(() => subscribeChatLock(() => setLockVer((n) => n + 1)), []);
  const results = React.useMemo(
    /* 🔒 a chat locked on the phone is left out of search until it is opened */
    () => searchAll({ term, chats: chats.filter((c) => !chatNeedsUnlock(c.id)), history: syncedHistory, previews, myUid }),
    [term, chats, syncedHistory, previews, myUid, lockVer],
  );

  useEffect(() => {
    if (!term.trim()) { setPeople([]); return; }
    let live = true;
    const t = setTimeout(() => searchUsers(term, myUid).then((r) => live && setPeople(r)), 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, myUid]);

  return (
    <div className="allo-sidebar">
      <div className="sidebar-top">
        {/* 🎬 the animated chat mark (theme-aware) — and the way home: tapping it closes
            the open conversation and brings the welcome pane back, which is the only
            route to it on a desktop layout where the chat never fully unmounts */}
        <button className="brand-home" onClick={onHome} title="Close this chat">
          <BrandLottie />
        </button>
        <div className="spacer" />
        <div className="sb-actions">
        <button className="icon-btn sb-act" title="New chat" aria-label="New chat" onClick={() => setStartChat(true)}>
          <Search width={18} height={18} />
        </button>
        {/* 🟢 THE CODE IS A SETTING, NOT A THING THAT ONLY HAPPENS TO YOU. Before this
            the only way anyone met the vault was the unlock screen appearing after an
            idle lock — nothing offered it, nothing changed it, and nothing took it
            away. A control that only ever interrupts you is one you resent. */}
        <VaultButton />
        <button className="icon-btn sb-act" title="My QR code" aria-label="My QR code" onClick={() => setShowQR(true)}>
          <QrIcon width={18} height={18} />
        </button>
        <div className="sb-menu-wrap" ref={menuRef}>
          <button
            className={`icon-btn sb-act ${menu ? 'lit' : ''}`} title="More"
            onClick={() => setMenu(!menu)} aria-label="More"
          >
            <More width={19} height={19} />
          </button>
          {menu && (
            <div className="sb-menu">
              <button onClick={() => { setMenu(false); setMakeGroup(true); }}>
                <GroupIcon width={15} height={15} /> Create group
              </button>
              <button onClick={() => { setMenu(false); setFilter(filter === 'unread' ? 'all' : 'unread'); }}>
                <Filter width={15} height={15} /> {filter === 'unread' ? 'Show all chats' : 'Filter unread'}
                {filter === 'unread' && <em className="on"><Check width={12} height={12} /></em>}
              </button>
              <button onClick={doMarkAll}>
                <CheckCheck width={15} height={15} /> Mark all read
              </button>
              {/* 🔗 the way to talk to someone who does not have Allo: a link
                  that lives for a day or two and then takes the whole
                  conversation with it */}
              <button onClick={() => { setMenu(false); setShowGuest(true); }}>
                <Link width={15} height={15} /> Chat with someone not on Allo
              </button>
              <button onClick={() => { setMenu(false); setShowBlocked(true); }}>
                <Slash width={15} height={15} /> Blocked users
                {blocking?.byMe?.size > 0 && <em>{blocking.byMe.size}</em>}
              </button>
              {/* 💬 HELP IS A CHAT, NOT A LINK OUT. The app's own Help entry has
                  said "coming soon" since it was written; this is where it goes.
                  Opening it is idempotent on the server — it creates the chat and
                  the welcome the first time and returns the existing one after —
                  so this button is safe to press twice. */}
              {/* 🟥 THE MENU CLOSES ON SUCCESS, NOT ON CLICK. It used to close
                  first and swallow the error, so a backend that was not
                  answering was indistinguishable from a button that was not
                  wired to anything — which is exactly how this looked for a
                  whole deploy cycle. A failure the user cannot see is a bug
                  report nobody can write. */}
              <button disabled={helpBusy} onClick={async () => {
                setNotice('');
                setHelpBusy(true);
                try {
                  const out = await openSupport();
                  setMenu(false);
                  onOpenChatId?.(out.chatId);
                } catch (e) {
                  const code = String(e?.message || '');
                  setNotice(
                    code === 'unauthenticated'
                      ? 'Sign in again to open Help.'
                      : 'Help is not reachable right now. Please try again.'
                  );
                } finally {
                  setHelpBusy(false);
                }
              }}>
                <Info width={15} height={15} /> {helpBusy ? 'Opening Help…' : 'Help'}
              </button>
            </div>
          )}
        </div>
        <button className="icon-btn sb-act danger" title="Log out" aria-label="Log out" onClick={onLogout}>
          <Logout width={18} height={18} />
        </button>
        </div>
      </div>

      <div className="search-wrap">
        <div className="search-box">
          <Search width={18} height={18} style={{ color: 'var(--text-3)' }} />
          <input placeholder="Search conversations" value={term} onChange={(e) => setTerm(e.target.value)} />
        </div>
      </div>

      {SYNC_LABEL[syncStatus] ? (
        /* 🟢 every number here is a loop counter somewhere real: the phone's export loop,
           the chunk fetcher, the per-chat decrypt. Nothing is animated for comfort.
           🟥 AND THE LABEL DECIDES, NOT THE PROGRESS. This used to render whenever
              `syncProgress?.text` existed, which outlived the status it described —
              so a finished sync could leave its own progress line on screen with
              nothing driving it. One source of truth: if there is no label for the
              current status, there is no pill. */
        <div className={`sync-pill ${SYNC_FAILED(syncStatus) ? 'stuck' : ''} ${syncStatus === 'synced' ? 'done' : ''}`}>
          <div className="sync-row">
            {/* a pulsing blue dot means "in progress". Once it has stalled it is not
                in progress, and pulsing at someone is how a wait becomes a lie. */}
            <span className={SYNC_FAILED(syncStatus) || syncStatus === 'synced' ? '' : 'pulse-dot'}
              style={{ width: 8, height: 8, borderRadius: 4, flex: 'none',
                background: SYNC_FAILED(syncStatus) ? '#e5484d'
                  : syncStatus === 'synced' ? '#30a46c' : 'var(--allo-blue)' }} />
            <span className="sync-text">{SYNC_LABEL[syncStatus]}</span>
            {syncProgress?.pct != null && <b>{syncProgress.pct}%</b>}
            {/* 🟢 THE ONE THING THEY CAN ACTUALLY DO. The old copy spent a whole
                sentence instructing the reader to go and operate their phone; a
                button that re-asks immediately is both shorter and more likely
                to be what fixes it — the common cause is an ask that arrived
                while the phone was busy, and asking again clears exactly that. */}
            {SYNC_FAILED(syncStatus) && onRetrySync && (
              <button type="button" className="sync-retry" onClick={onRetrySync}>Retry</button>
            )}
          </div>
          {syncProgress?.pct != null && (
            <div className="sync-bar"><i style={{ width: `${syncProgress.pct}%` }} /></div>
          )}
        </div>
      ) : null}

      {!term.trim() && (
        <div className="sb-lists">
          <button className={`sb-list ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>
            All
          </button>
          <button className={`sb-list ${filter === 'unread' ? 'on' : ''}`} onClick={() => setFilter('unread')}>
            Unread
            {(() => {
              const n = chats.filter((c) => !isArchived(c, myUid) && hasUnread(c)).length;
              return n ? <em>{n}</em> : null;
            })()}
          </button>
          <button className={`sb-list ${filter === 'groups' ? 'on' : ''}`} onClick={() => setFilter('groups')}>
            Groups
          </button>
          {lists.map((l) => (
            <button
              key={l.id}
              className={`sb-list ${filter === l.id ? 'on' : ''}`}
              onClick={() => setFilter(l.id)}
              onDoubleClick={() => setEditList(l)}
              title="Double-click to manage"
            >
              {l.name}
              {filter === l.id && (
                <span
                  className="sb-list-edit" role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setEditList(l); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setEditList(l); } }}
                  title="Manage list"
                >
                  <Gear width={12} height={12} />
                </span>
              )}
            </button>
          ))}
          <button className="sb-list add" onClick={() => setEditList({})} title="New list">+</button>
        </div>
      )}

      <div
        className="chat-list"
        onScroll={(e) => {
          // 🟢 the nav rail's hide/show signal: direction only, throttled by delta so a
          //    trackpad's sub-pixel jitter can't strobe it. Near the top it always shows.
          const el = e.currentTarget;
          const last = el._lastTop || 0;
          const d = el.scrollTop - last;
          if (Math.abs(d) < 6) return;
          el._lastTop = el.scrollTop;
          if (el.scrollTop < 40) onListScroll?.('up');
          else onListScroll?.(d > 0 ? 'down' : 'up');
        }}
      >
        {term.trim() && (
          <SearchResults
            term={term}
            results={results}
            chats={chats}
            people={people}
            myUid={myUid}
            onOpenChat={(c) => { onSelectChat(c); setTerm(''); }}
            onStartChat={(u) => { onStartChat(u); setTerm(''); }}
            onOpenMessage={(h) => {
              onSelectChat(h.chat);
              setTerm('');
              // ask the open conversation to scroll to it — it obliges when the
              // message is in its loaded window, and says so when it isn't
              if (h.messageId && !h.messageId.startsWith('pv-')) {
                setTimeout(() => window.dispatchEvent(new CustomEvent('allo:jump-message', {
                  detail: { chatId: h.chatId, messageId: h.messageId },
                })), 260);
              }
            }}
          />
        )}

        {!term.trim() && visibleChats.map((c) => (
          <ChatRow key={c.id} chat={c} myUid={myUid} myName={myName} myPrivacy={myPrivacy} active={c.id === selectedChatId} onClick={() => onSelectChat(c)} onClosed={onHome} syncedForChat={syncedHistory?.[c.id]} draggable={draggableRows} preview={previews[c.id]} blocking={blocking} myReadAt={myReads[c.id]} syncing={historyPending} typers={(typing[c.id] || []).filter((t) => t.uid !== myUid)} />
        ))}

        {/* ── archived: folded away at the bottom, like the app ── */}
        {!term.trim() && archivedChats.length > 0 && (
          <>
            <button className="arch-toggle" onClick={() => setShowArchived(!showArchived)}>
              <ArchiveIcon width={15} height={15} />
              Archived
              <b>{archivedChats.length}</b>
              <ChevronRight width={14} height={14} style={{ transform: showArchived ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
            </button>
            {showArchived && archivedChats.map((c) => (
              <ChatRow key={c.id} chat={c} myUid={myUid} myName={myName} myPrivacy={myPrivacy} active={c.id === selectedChatId} onClick={() => onSelectChat(c)} onClosed={onHome} syncedForChat={syncedHistory?.[c.id]} draggable={draggableRows} preview={previews[c.id]} blocking={blocking} myReadAt={myReads[c.id]} syncing={historyPending} typers={(typing[c.id] || []).filter((t) => t.uid !== myUid)} />
            ))}
          </>
        )}

        {!term.trim() && filter !== 'all' && visibleChats.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
            {filter === 'unread' ? 'Nothing unread.'
              : filter === 'groups' ? 'No groups yet.'
                : 'Nothing in this list yet.'}
          </div>
        )}

        {!term.trim() && filter === 'all' && chats.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
            No chats yet. Search a name above to start one.
          </div>
        )}
      </div>

      {(marking || notice) && (
        <div className="sb-toast">
          {marking === 'working' ? 'Marking everything read…' : (marking || notice)}
        </div>
      )}

      {makeGroup && (
        <NewGroupModal
          me={{ uid: myUid, name: myName, image: myProfile?.profileImage || '' }}
          initialMembers={[]}
          onClose={() => setMakeGroup(false)}
          onCreated={(gid) => onOpenChatId?.(gid)}
        />
      )}
      {editList && (
        <ListEditorModal
          myUid={myUid}
          chats={chats}
          list={editList.id ? lists.find((l) => l.id === editList.id) || editList : null}
          onClose={() => setEditList(null)}
          onCreated={(id) => setFilter(id)}
          onDeleted={() => setFilter('all')}
        />
      )}
      {showQR && (
        <MyQRModal myUid={myUid} myProfile={myProfile} onClose={() => setShowQR(false)} />
      )}
      {startChat && (
        <StartChatModal
          myUid={myUid}
          onPick={(u) => onStartChat(u)}
          onPickSelf={() => {
            // an existing Note to Self wins over creating a second one
            const mine = chats.find((c) => isNoteToSelf(c, myUid));
            if (mine) onSelectChat(mine);
            else onStartChat({ uid: myUid, fullName: 'Note to Self', username: 'Me' });
          }}
          onClose={() => setStartChat(false)}
        />
      )}
      {showBlocked && (
        <BlockedUsersModal myUid={myUid} blocking={blocking} onClose={() => setShowBlocked(false)} />
      )}
      {showGuest && (
        <GuestLinkModal myUid={myUid} onClose={() => setShowGuest(false)} />
      )}
    </div>
  );
}
